import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useBookings, type BookingFilter } from '../api/queries';
import { Empty, ErrorNote, Icon, Loading } from '../ui/kit';
import { BookingItem, PageHead } from './shared';

type View = 'arriving' | 'inhouse' | 'departing' | 'upcoming' | 'all';

const VIEWS: { id: View; label: string }[] = [
  { id: 'arriving', label: 'Arriving today' },
  { id: 'inhouse', label: 'In house' },
  { id: 'departing', label: 'Departing today' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'all', label: 'All' },
];

export function Bookings() {
  const { me, can } = useAuth();
  const today = me?.property.today ?? '';
  const [view, setView] = useState<View>('arriving');
  const [ref, setRef] = useState('');

  const searching = ref.trim().length >= 3;
  const filter: BookingFilter = searching
    ? { search: ref.trim(), limit: 50 }
    : view === 'arriving'
      ? { arrivingOn: today, limit: 100 }
      : view === 'inhouse'
        ? { status: 'CHECKED_IN', limit: 200 }
        : view === 'departing'
          ? { departingOn: today, limit: 100 }
          : view === 'upcoming'
            ? { status: 'CONFIRMED', limit: 100 }
            : { limit: 100 };

  const q = useBookings(filter);

  return (
    <div className="page">
      <PageHead eyebrow="Front desk" title="Bookings">
        {can('booking.create') ? (
          <Link to="/new" className="btn primary">
            New booking
          </Link>
        ) : null}
      </PageHead>

      <div className="stack">
        <div className="seg" role="group" aria-label="Show">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" aria-pressed={!searching && view === v.id} onClick={() => { setRef(''); setView(v.id); }}>
              {v.label}
            </button>
          ))}
        </div>
        <div className="field">
          <label htmlFor="ref" className="sr-only">
            Find a booking by guest name, phone number or reference
          </label>
          <div style={{ position: 'relative' }}>
            <input id="ref" className="input" placeholder="Find by guest name, phone number or reference" value={ref} onChange={(e) => setRef(e.target.value)} autoCapitalize="characters" autoComplete="off" />
            <span style={{ position: 'absolute', right: 14, top: 12, width: 22, height: 22, color: 'var(--ink-3)', pointerEvents: 'none' }}>
              <Icon name="search" />
            </span>
          </div>
        </div>
      </div>

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorNote error={q.error} retry={() => void q.refetch()} />
      ) : (q.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="bookings" title={searching ? 'No booking found' : 'Nothing here'}>
            {searching ? 'Check the spelling or number and try again. Part of a name or the last digits of a phone number is enough.' : 'Bookings that match this view will appear here.'}
          </Empty>
        </div>
      ) : (
        <div className="list">
          {(q.data ?? []).map((b) => (
            <BookingItem key={b.id} b={b} />
          ))}
        </div>
      )}
    </div>
  );
}
